import React, { useContext, useEffect } from "react";
import { ArrowRightIcon, Button, ChevronStartIcon, Flex, PollIcon } from "@fluentui/react-northstar";
import { TokenType, LayoutType, SectionVisibility } from "powerbi-models";
import { Report } from "powerbi-client";
import { PowerBiReportPagesDropdown, PowerBiReportsInitializingLoader } from "./components";
import { PowerBiReportsContext } from "./store";
import styles from "./PowerBiReportsTab.module.scss";
import { MsTeamsContext } from "msTeams/store";
import { BasicTable, RegularText, SegmentContent, TextTableHeader, Title } from "shared/components";
import { PowerBiReport } from "./models";
import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { trackEvent } from "appilication-insights/ApplicationInsightsUtils";
import { OpenPowerBiReportEvent } from "appilication-insights/events/OpenPowerBiReport/OpenPowerBiReportEvent";
import pl from "shared/messages/pl";
import classNames from "classnames";
import { EmbedType, PowerBIEmbed } from "powerbi-client-react";
import EmptyContent from "view360Card/components/emptyContent/EmptyContent";

const PowerBiReportsTab: React.FC = () => {
    const powerBiReportsContext = useContext(PowerBiReportsContext);
    const msTeamsContext = useContext(MsTeamsContext);
    const appInsightsContext = useAppInsightsContext();

    useEffect(() => {
        const initialize = async () => {
            await powerBiReportsContext.initialize();
        };

        if (powerBiReportsContext.initialized === false) {
            initialize();
        }
    }, []);

    if (powerBiReportsContext.initialized === false) {
        return <PowerBiReportsInitializingLoader />;
    }

    const getRows = () => {
        return powerBiReportsContext.reports.map((powerBiReport) => {
            return {
                className: msTeamsContext.currentMsTeamsThemeName === "default" ? styles.defaultRow : styles.darkRow,
                onClick: () => showReport(powerBiReport),
                key: powerBiReport.id,
                items: getItems(powerBiReport),
            };
        });
    };

    const getItems = (powerBiReport: PowerBiReport) => {
        let items = [
            {
                className: styles.columnTypeIcon,
                key: `${powerBiReport.id}-type`,
                content: <PollIcon outline size="medium" />,
            },
            {
                key: `${powerBiReport.id}-name`,
                content: <RegularText content={powerBiReport.name} />,
            },
        ];

        if (msTeamsContext.isMobileClient) {
            items.push({
                className: styles.columnAction,
                key: `${powerBiReport.id}-action`,
                content: (
                    <Button
                        size="small"
                        primary
                        iconOnly
                        icon={<ArrowRightIcon size="small" />}
                        onClick={() => {
                            showReport(powerBiReport);
                        }}
                    />
                ),
            });
        }
        return items;
    };

    const getHeaderItems = () => {
        let headerItems = [
            {
                className: styles.columnTypeIcon,
                content: <TextTableHeader content="" />,
                key: "type",
            },
            {
                content: <TextTableHeader content="Nazwa raportu" />,
                key: "name",
            },
        ];

        if (msTeamsContext.isMobileClient) {
            headerItems.push({
                className: styles.columnAction,
                content: <TextTableHeader content="Akcje" />,
                key: "actions",
            });
        }

        return headerItems;
    };

    const showReport = (powerBiReport: PowerBiReport) => {
        const ev = new OpenPowerBiReportEvent(powerBiReport);
        trackEvent(appInsightsContext, ev, ev.getCustomProperties());
        powerBiReportsContext.setSelectedReport(powerBiReport);
    };

    return (
        <>
            {!powerBiReportsContext.selectedReport && (
                <div className={styles.card}>
                    <SegmentContent>
                        {powerBiReportsContext.reports && powerBiReportsContext.reports.length > 0 ? (
                            <BasicTable rows={getRows()} headerItems={getHeaderItems()} />
                        ) : (
                            <EmptyContent message={pl.powerBiReports.noReports} graphic="welcome" />
                        )}
                    </SegmentContent>
                </div>
            )}
            {powerBiReportsContext.selectedReport && (
                <Flex
                    fill
                    column
                    gap="gap.small"
                    // TODO: EHTA-486 - additional class and style is a quick fix for report better visibility. hamburgermenu shoudl fix it permanently
                    styles={({ theme: { siteVariables } }) => ({
                        backgroundColor: siteVariables.colorScheme.default.background2,
                    })}
                    className={classNames(styles.tab, { [styles.mobileRaportPosition]: msTeamsContext.isMobileClient })}
                >
                    <Flex vAlign="center" gap="gap.small">
                        <Button
                            iconOnly={true}
                            icon={<ChevronStartIcon size="large" />}
                            onClick={() => {
                                powerBiReportsContext.setSelectedReport(undefined);
                                powerBiReportsContext.setSelectedReportPages(undefined);
                                powerBiReportsContext.setSelectedReportPage(undefined);
                            }}
                        />
                        <div style={{ marginTop: "2px" }}>
                            <Title text={powerBiReportsContext.selectedReport.name} isHeader />
                        </div>
                    </Flex>
                    {msTeamsContext.isMobileClient && (
                        <PowerBiReportPagesDropdown
                            selectedPage={powerBiReportsContext.selectedReportPage}
                            availablePages={powerBiReportsContext.selectedReportPages ?? []}
                            loading={powerBiReportsContext.selectedReportPages === undefined}
                            onPageSelected={(page) => {
                                powerBiReportsContext.setSelectedReportPage(page);
                                page.setActive();
                            }}
                        />
                    )}
                    <Flex hAlign="center">
                        <PowerBIEmbed
                            embedConfig={{
                                type: EmbedType.Report,
                                id: powerBiReportsContext.selectedReport.id,
                                embedUrl: powerBiReportsContext.selectedReport.embedUrl,
                                tokenType: TokenType.Aad,
                                accessToken: powerBiReportsContext.powerBiAccessToken,
                                settings: {
                                    layoutType: msTeamsContext.isMobileClient
                                        ? LayoutType.MobilePortrait
                                        : LayoutType.Master,
                                    panes: {
                                        filters: {
                                            expanded: false,
                                        },
                                    },
                                },
                            }}
                            eventHandlers={
                                new Map([
                                    [
                                        "loaded",
                                        (_, entity) => {
                                            const currentReport = entity as Report;
                                            if (currentReport) {
                                                currentReport.getPages().then((currentReportPages) => {
                                                    const visiblePages = currentReportPages.filter(
                                                        (page) => page.visibility === SectionVisibility.AlwaysVisible
                                                    );
                                                    powerBiReportsContext.setSelectedReportPages(visiblePages);
                                                    if (visiblePages.length > 0) {
                                                        const firstPage = visiblePages[0];
                                                        firstPage.setActive();
                                                        powerBiReportsContext.setSelectedReportPage(firstPage);
                                                    }
                                                });
                                            }
                                        },
                                    ],
                                ])
                            }
                            cssClassName={
                                msTeamsContext.isMobileClient ? styles.powerbiEmbedMobile : styles.powerbiEmbed
                            }
                        />
                    </Flex>
                </Flex>
            )}
        </>
    );
};

export default PowerBiReportsTab;
